import {
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DisplayDateTime } from "components/DisplayDate";

const READ_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "Unread", value: "unread" },
  { label: "Read", value: "read" },
];

const AccountNotificationsPopover = ({
  anchorEl,
  open,
  onClose,
  notifications = [],
  isLoading = false,
  onRefresh,
  onClickNotification,
  markingReadId,
  readEnum = "ALL",
  onReadFilterChange,
  page = 0,
  totalPages = 1,
  onPageChange,
}) => {
  const pages = Math.max(totalPages, 1);
  const currentPage = page + 1;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <Box sx={{ width: 440, maxWidth: "92vw" }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "background.default",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Account notifications
          </Typography>
          <Button size="small" variant="outlined" onClick={onRefresh} disabled={isLoading}>
            Refresh
          </Button>
        </Box>

        <Box sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={readEnum}
            onChange={(_, v) => {
              if (v) onReadFilterChange?.(v);
            }}
          >
            {READ_OPTIONS.map((o) => (
              <ToggleButton key={o.value} value={o.value} sx={{ textTransform: "none", py: 0.25 }}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <List dense disablePadding sx={{ maxHeight: 480, overflowY: "auto" }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {isLoading ? "Loading..." : "No account notifications."}
              </Typography>
            </Box>
          ) : (
            notifications.map((item, idx) => (
              <Box key={item?.id ?? idx}>
                <ListItemButton
                  onClick={() => onClickNotification?.(item)}
                  sx={{
                    alignItems: "flex-start",
                    bgcolor: item?.read ? "transparent" : "rgba(148, 163, 184, 0.18)",
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={item?.read ? 500 : 700}>
                        {item?.content || "—"}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        <DisplayDateTime value={item?.date || new Date()} />
                        {markingReadId === item?.id ? " · Marking as read..." : ""}
                      </Typography>
                    }
                  />
                </ListItemButton>
                {idx < notifications.length - 1 && <Divider />}
              </Box>
            ))
          )}
        </List>

        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "background.default",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            size="small"
            disabled={page <= 0 || isLoading}
            onClick={() => onPageChange?.(page - 1)}
          >
            Prev
          </Button>
          <Typography variant="caption" color="text.secondary">
            Page {currentPage} of {pages}
          </Typography>
          <Button
            size="small"
            disabled={currentPage >= pages || isLoading}
            onClick={() => onPageChange?.(page + 1)}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default AccountNotificationsPopover;
